import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, ChevronRight, RefreshCw, Sparkles, Target } from 'lucide-react';
import api from '@/lib/api';

/**
 * Smarto Setup Score — circular progress ring with next-3 suggested actions.
 *
 * Polls /api/admin/setup-score on mount. Each call is server-side cached
 * for 5 minutes so this is cheap. Renders `null` until first response so
 * we don't flicker an empty ring.
 */
const SIZE = 110;
const STROKE = 9;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

function _ringTone(score) {
  if (score >= 90) return { stroke: '#10B981', tint: 'emerald', label: 'All set' };
  if (score >= 60) return { stroke: '#F59E0B', tint: 'amber', label: 'Getting there' };
  if (score >= 30) return { stroke: '#F97316', tint: 'orange', label: 'Just starting' };
  return { stroke: '#EF4444', tint: 'rose', label: 'Setup needed' };
}

export const SetupScoreRing = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    try {
      const r = await api.get(`/admin/setup-score${force ? '?force_refresh=true' : ''}`);
      setData(r.data);
    } catch (e) {
      // Silent fail — widget hides itself when data is null
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  if (loading) {
    return (
      <Card className="border-border" data-testid="setup-score-loading">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-[110px] h-[110px] rounded-full bg-stone-100 dark:bg-stone-800 animate-pulse flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 rounded bg-stone-100 dark:bg-stone-800 animate-pulse" />
            <div className="h-2 w-44 rounded bg-stone-100 dark:bg-stone-800 animate-pulse" />
            <div className="h-2 w-40 rounded bg-stone-100 dark:bg-stone-800 animate-pulse" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;
  if (data.score >= 100) {
    // Hide once fully complete — celebrate then disappear after the user reloads
    return (
      <Card className="border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20" data-testid="setup-score-complete">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
            <CheckCircle2 size={20} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold font-outfit">Setup complete · 100%</p>
            <p className="text-xs text-muted-foreground">Every onboarding signal checked off. You're production-ready.</p>
          </div>
          <Sparkles size={16} className="text-emerald-500 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  const tone = _ringTone(data.score);
  const dashOffset = CIRC * (1 - data.score / 100);

  return (
    <Card className="border-border" data-testid="setup-score-ring">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Ring */}
          <div className="relative flex-shrink-0" style={{ width: SIZE, height: SIZE }}>
            <svg width={SIZE} height={SIZE} className="-rotate-90" aria-hidden="true">
              <circle
                cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
                fill="none" stroke="currentColor" strokeWidth={STROKE}
                className="text-stone-200 dark:text-stone-700"
              />
              <circle
                cx={SIZE / 2} cy={SIZE / 2} r={RADIUS}
                fill="none" stroke={tone.stroke} strokeWidth={STROKE}
                strokeDasharray={CIRC} strokeDashoffset={dashOffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 600ms ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold font-outfit tabular-nums" data-testid="setup-score-value">
                {data.score}%
              </span>
              <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                {data.completed_count}/{data.total_signals}
              </span>
            </div>
          </div>
          {/* Suggestions */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <Target size={14} className="text-orange-500 flex-shrink-0" />
                <h3 className="text-sm font-bold font-outfit truncate">Setup Score</h3>
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full whitespace-nowrap bg-${tone.tint}-100 text-${tone.tint}-700 dark:bg-${tone.tint}-900/30 dark:text-${tone.tint}-300`}>
                  {tone.label}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setRefreshing(true); load(true); }}
                disabled={refreshing}
                data-testid="setup-score-refresh-btn"
                className="text-muted-foreground hover:text-foreground transition disabled:opacity-50"
                aria-label="Refresh setup score"
                title="Refresh"
              >
                <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              </button>
            </div>
            {(data.next_actions || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">All onboarding tasks complete. Nice work.</p>
            ) : (
              <ul className="space-y-1" data-testid="setup-score-suggestions">
                {data.next_actions.map((step) => (
                  <li key={step.key}>
                    <Link
                      to={step.path}
                      data-testid={`setup-score-action-${step.key}`}
                      className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-stone-50 dark:hover:bg-stone-800/50 transition group"
                    >
                      <span className="text-xs text-foreground group-hover:font-medium truncate">
                        {step.label}
                      </span>
                      <ChevronRight size={12} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition flex-shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SetupScoreRing;
